/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Alert,
  Dimensions,
  Linking,
  Share,
  Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import MapView, { Marker } from 'react-native-maps';

const { width, height } = Dimensions.get('window');

const UniversityDetails = () => {
  const navigation = useNavigation();
  const route = useRoute();
  
  // Get the university from route params
  const { university } = route.params || {};

  // Coerce DB string fields (comma-separated) or arrays into a string array.
  const toArray = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
      const s = val.trim();
      if (!s) return [];
      if (s.startsWith('[')) { try { const p = JSON.parse(s); if (Array.isArray(p)) return p; } catch {} }
      return s.split(',').map((x) => x.trim()).filter(Boolean);
    }
    return [];
  };

  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const mapRef = useRef(null);

  // University coordinates mapping
  const universityCoordinates = {
    'University of Cambridge': { latitude: 52.2053, longitude: 0.1218 },
    'University of Oxford': { latitude: 51.7548, longitude: -1.2544 },
    'Imperial College London': { latitude: 51.4988, longitude: -0.1749 },
    'University of Manchester': { latitude: 53.4668, longitude: -2.2339 },
    'University of Edinburgh': { latitude: 55.9470, longitude: -3.1870 },
    'University of Bristol': { latitude: 51.4560, longitude: -2.6044 },
    'University of Sheffield': { latitude: 53.3811, longitude: -1.4801 },
    'University of Leeds': { latitude: 53.8067, longitude: -1.5550 },
    'University of Glasgow': { latitude: 55.8721, longitude: -4.2882 },
    'University of Southampton': { latitude: 50.9361, longitude: -1.3961 },
  };

  useEffect(() => {
    loadDarkMode();
    checkFavoriteStatus();
  }, []);

  const loadDarkMode = async () => {
    try {
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
    } catch (error) {
      console.error('Error loading dark mode:', error);
    }
  };

  const checkFavoriteStatus = async () => {
    try {
      const favorites = await AsyncStorage.getItem('favoriteUniversities');
      if (favorites) {
        const favoriteList = JSON.parse(favorites);
        setIsFavorite(favoriteList.includes(university.id));
      }
    } catch (error) {
      console.error('Error checking favorite status:', error);
    }
  };

  const toggleFavorite = async () => {
    try {
      const favorites = await AsyncStorage.getItem('favoriteUniversities');
      let favoriteList = favorites ? JSON.parse(favorites) : [];
      
      if (isFavorite) {
        favoriteList = favoriteList.filter(id => id !== university.id);
      } else {
        favoriteList.push(university.id);
      }
      
      await AsyncStorage.setItem('favoriteUniversities', JSON.stringify(favoriteList));
      setIsFavorite(!isFavorite);
      
      Alert.alert(
        isFavorite ? 'Removed from Favorites' : 'Added to Favorites',
        `${university.name} has been ${isFavorite ? 'removed from' : 'added to'} your favorites.`
      );
    } catch (error) {
      console.error('Error toggling favorite:', error);
      Alert.alert('Error', 'Failed to update favorites.');
    }
  };

  const openWebsite = async () => {
    try {
      await Linking.openURL(university.website);
    } catch (error) {
      console.error('Error opening URL:', error);
      Alert.alert('Error', 'Failed to open website.');
    }
  };

  const shareUniversity = async () => {
    try {
      await Share.share({
        title: `Check out ${university.name}`,
        message: `${university.name} - ${university.rankingLabel}\n\nLocation: ${university.location}\nTuition: ${university.intlTuition}\nDeadline: ${university.applicationDeadline}\n\n${university.description}`,
      });
    } catch (error) {
      console.error('Error sharing university:', error);
    }
  };

  const callAdmissions = () => {
    // Mock phone number
    const phoneNumber = '+441223338000';
    Linking.openURL(`tel:${phoneNumber}`).catch(err => {
      Alert.alert('Error', 'Cannot make phone call from this device.');
    });
  };

  const navigateToApplication = () => {
    navigation.navigate('UniversityApply', { university });
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const renderHeader = () => (
    <LinearGradient
      colors={[university.color, `${university.color}80`]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 0 }}
      style={styles.header}
    >
      <TouchableOpacity 
        style={styles.backButton}
        onPress={() => navigation.goBack()}
      >
        <MaterialCommunityIcons name="arrow-left" size={24} color="#ffffff" />
      </TouchableOpacity>
      
      <View style={styles.headerTitleContainer}>
        <Text style={styles.headerTitle}>{university.name}</Text>
        <Text style={styles.headerSubtitle}>
          <MaterialCommunityIcons name="map-marker" size={12} color="rgba(255,255,255,0.9)" />
          {' '}{university.location}
        </Text>
      </View>
      
      <TouchableOpacity 
        style={styles.favoriteButton}
        onPress={toggleFavorite}
      >
        <MaterialCommunityIcons 
          name={isFavorite ? "heart" : "heart-outline"} 
          size={24} 
          color={isFavorite ? "#ef4444" : "#ffffff"} 
        />
      </TouchableOpacity>
    </LinearGradient>
  );

  const renderRankingBadge = () => (
    <View style={styles.rankingContainer}>
      <LinearGradient
        colors={['#fbbf24', '#f59e0b']}
        style={styles.rankingBadge}
      >
        <Text style={styles.rankingNumber}>#{university.ranking}</Text>
        <Text style={styles.rankingLabel}>UK Rank</Text>
      </LinearGradient>
      
      <View style={styles.rankingInfo}>
        <Text style={styles.rankingText}>{university.rankingLabel}</Text>
        <Text style={styles.worldRanking}>World Ranking: #{university.worldRanking}</Text>
      </View>
    </View>
  );

  const renderQuickStats = () => (
    <View style={styles.quickStatsContainer}>
      <View style={styles.statItem}>
        <MaterialCommunityIcons name="school" size={20} color={darkMode ? "#a0aec0" : "#6b7280"} />
        <Text style={styles.statValue}>{university.studentPopulation?.toLocaleString() || 'N/A'}</Text>
        <Text style={styles.statLabel}>Students</Text>
      </View>
      
      <View style={styles.statDivider} />
      
      <View style={styles.statItem}>
        <MaterialCommunityIcons name="cash" size={20} color={darkMode ? "#a0aec0" : "#6b7280"} />
        <Text style={styles.statValue}>{university.intlTuition?.split('-')[0] || 'N/A'}</Text>
        <Text style={styles.statLabel}>Tuition</Text>
      </View>
      
      <View style={styles.statDivider} />
      
      <View style={styles.statItem}>
        <MaterialCommunityIcons name="calendar" size={20} color={darkMode ? "#a0aec0" : "#6b7280"} />
        <Text style={styles.statValue}>{university.applicationDeadline || 'N/A'}</Text>
        <Text style={styles.statLabel}>Deadline</Text>
      </View>
      
      <View style={styles.statDivider} />
      
      <View style={styles.statItem}>
        <MaterialCommunityIcons name="briefcase" size={20} color={darkMode ? "#a0aec0" : "#6b7280"} />
        <Text style={styles.statValue}>{university.placementRate || 'N/A'}</Text>
        <Text style={styles.statLabel}>Placement</Text>
      </View>
    </View>
  );

  const renderActionButtons = () => (
    <View style={styles.actionButtonsContainer}>
      <TouchableOpacity
        style={[styles.actionButton, styles.websiteButton]}
        onPress={openWebsite}
      >
        <MaterialCommunityIcons name="web" size={20} color="#ffffff" />
        <Text style={styles.actionButtonText}>Website</Text>
      </TouchableOpacity>
      
      <TouchableOpacity
        style={[styles.actionButton, styles.applyButton]}
        onPress={navigateToApplication}
      >
        <MaterialCommunityIcons name="send" size={20} color="#ffffff" />
        <Text style={styles.actionButtonText}>Apply Now</Text>
      </TouchableOpacity>
      
      <TouchableOpacity
        style={[styles.actionButton, styles.shareButton]}
        onPress={shareUniversity}
      >
        <MaterialCommunityIcons name="share-variant" size={20} color="#ffffff" />
        <Text style={styles.actionButtonText}>Share</Text>
      </TouchableOpacity>
    </View>
  );

  const renderDescription = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>About</Text>
      <Text style={styles.descriptionText}>{university.description}</Text>
    </View>
  );

  const renderSpecializations = () => (
    <View style={styles.section}>
      <TouchableOpacity 
        style={styles.sectionHeader}
        onPress={() => toggleSection('specializations')}
      >
        <Text style={styles.sectionTitle}>Engineering Specializations</Text>
        <MaterialCommunityIcons 
          name={expandedSection === 'specializations' ? "chevron-up" : "chevron-down"} 
          size={24} 
          color={darkMode ? "#a0aec0" : "#6b7280"} 
        />
      </TouchableOpacity>
      
      {expandedSection === 'specializations' && (
        <View style={styles.specializationsContainer}>
          {toArray(university.specializations).map((spec, index) => (
            <View key={index} style={styles.specializationItem}>
              <MaterialCommunityIcons name="wrench" size={16} color="#667eea" />
              <Text style={styles.specializationText}>{spec}</Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );

  const renderAdmissionDetails = () => (
    <View style={styles.section}>
      <TouchableOpacity 
        style={styles.sectionHeader}
        onPress={() => toggleSection('admissions')}
      >
        <Text style={styles.sectionTitle}>Admission Details</Text>
        <MaterialCommunityIcons 
          name={expandedSection === 'admissions' ? "chevron-up" : "chevron-down"} 
          size={24} 
          color={darkMode ? "#a0aec0" : "#6b7280"} 
        />
      </TouchableOpacity>
      
      {expandedSection === 'admissions' && (
        <View style={styles.detailsContainer}>
          <DetailRow 
            icon="book-open"
            label="Entry Requirements"
            value={university.entryRequirements || 'N/A'}
          />
          <DetailRow 
            icon="calendar-clock"
            label="Application Deadline"
            value={university.applicationDeadline || 'N/A'}
          />
          <DetailRow 
            icon="percent"
            label="Acceptance Rate"
            value={university.acceptanceRate || 'N/A'}
          />
          <DetailRow 
            icon="cash"
            label="UK Tuition"
            value={university.tuition || 'N/A'}
          />
          <DetailRow 
            icon="cash"
            label="International Tuition"
            value={university.intlTuition || 'N/A'}
          />
          <TouchableOpacity 
            style={styles.contactButton}
            onPress={callAdmissions}
          >
            <MaterialCommunityIcons name="phone" size={16} color="#ffffff" />
            <Text style={styles.contactButtonText}>Contact Admissions Office</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );

  const renderStudentLife = () => (
    <View style={styles.section}>
      <TouchableOpacity 
        style={styles.sectionHeader}
        onPress={() => toggleSection('studentLife')}
      >
        <Text style={styles.sectionTitle}>Student Life & Stats</Text>
        <MaterialCommunityIcons 
          name={expandedSection === 'studentLife' ? "chevron-up" : "chevron-down"} 
          size={24} 
          color={darkMode ? "#a0aec0" : "#6b7280"} 
        />
      </TouchableOpacity>
      
      {expandedSection === 'studentLife' && (
        <View style={styles.statsGrid}>
          <StatCard 
            value={university.studentSatisfaction || 'N/A'}
            label="Student Satisfaction"
            icon="emoticon-happy"
          />
          <StatCard 
            value={university.researchRating || 'N/A'}
            label="Research Rating"
            icon="chart-line"
          />
          <StatCard 
            value={university.avgSalary || 'N/A'}
            label="Avg Graduate Salary"
            icon="currency-gbp"
          />
          <StatCard 
            value={university.internationalStudents || 'N/A'}
            label="International Students"
            icon="earth"
          />
          <StatCard 
            value={university.campusSize || 'N/A'}
            label="Campus Size"
            icon="home-city"
          />
          <StatCard 
            value={university.founded || 'N/A'}
            label="Founded"
            icon="history"
          />
        </View>
      )}
    </View>
  );

  const renderNotableAlumni = () => {
    if (!university.notableAlumni) return null;
    
    const alumniList = toArray(university.notableAlumni);
    
    return (
      <View style={styles.section}>
        <TouchableOpacity 
          style={styles.sectionHeader}
          onPress={() => toggleSection('alumni')}
        >
          <Text style={styles.sectionTitle}>Notable Alumni</Text>
          <MaterialCommunityIcons 
            name={expandedSection === 'alumni' ? "chevron-up" : "chevron-down"} 
            size={24} 
            color={darkMode ? "#a0aec0" : "#6b7280"} 
          />
        </TouchableOpacity>
        
        {expandedSection === 'alumni' && (
          <View style={styles.alumniContainer}>
            {alumniList.map((alumnus, index) => (
              <View key={index} style={styles.alumnusItem}>
                <MaterialCommunityIcons name="account-tie" size={16} color="#667eea" />
                <Text style={styles.alumnusText}>{alumnus}</Text>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  const renderUniversityMap = () => {
    const coordinates = universityCoordinates[university.name];
    if (!coordinates) return null;

    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${coordinates.latitude},${coordinates.longitude}`;

    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Location</Text>
        <View style={styles.mapContainer}>
          {Platform.OS === 'web' ? (
            // Web: react-native-maps is native-only, so use a keyless Google Maps embed.
            // react-native-web renders via react-dom, so React.createElement('iframe', ...) yields a real DOM iframe.
            React.createElement('iframe', {
              title: 'University location',
              src: `https://maps.google.com/maps?q=${coordinates.latitude},${coordinates.longitude}&z=14&output=embed`,
              style: { border: 0, width: '100%', height: '100%' },
              loading: 'lazy',
              allowFullScreen: true,
            })
          ) : (
            <MapView
              ref={mapRef}
              style={styles.map}
              initialRegion={{
                ...coordinates,
                latitudeDelta: 0.05,
                longitudeDelta: 0.05,
              }}
            >
              <Marker coordinate={coordinates}>
                <View style={styles.mapMarker}>
                  <LinearGradient
                    colors={[university.color, `${university.color}80`]}
                    style={styles.markerGradient}
                  >
                    <MaterialCommunityIcons name="school" size={20} color="#ffffff" />
                  </LinearGradient>
                </View>
              </Marker>
            </MapView>
          )}
          <TouchableOpacity 
            style={styles.directionsButton}
            onPress={() => {
              Linking.openURL(directionsUrl);
            }}
          >
            <MaterialCommunityIcons name="directions" size={20} color="#ffffff" />
            <Text style={styles.directionsButtonText}>Get Directions</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  const renderTags = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>University Features</Text>
      <View style={styles.tagsContainer}>
        {toArray(university.tags).map((tag, index) => (
          <View key={index} style={styles.tag}>
            <Text style={styles.tagText}>{tag}</Text>
          </View>
        ))}
        {university.isRussellGroup && (
          <View style={[styles.tag, styles.russellTag]}>
            <MaterialCommunityIcons name="shield-check" size={14} color="#ffffff" />
            <Text style={[styles.tagText, styles.russellTagText]}>Russell Group</Text>
          </View>
        )}
        {university.hasScholarships && (
          <View style={[styles.tag, styles.scholarshipTag]}>
            <MaterialCommunityIcons name="gift" size={14} color="#ffffff" />
            <Text style={[styles.tagText, styles.scholarshipTagText]}>Scholarships Available</Text>
          </View>
        )}
      </View>
    </View>
  );

  const DetailRow = ({ icon, label, value }) => (
    <View style={styles.detailRow}>
      <View style={styles.detailIconContainer}>
        <MaterialCommunityIcons name={icon} size={16} color="#667eea" />
      </View>
      <View style={styles.detailContent}>
        <Text style={styles.detailLabel}>{label}</Text>
        <Text style={styles.detailValue}>{value}</Text>
      </View>
    </View>
  );

  const StatCard = ({ value, label, icon }) => (
    <View style={styles.statCard}>
      <View style={styles.statIconContainer}>
        <MaterialCommunityIcons name={icon} size={20} color="#667eea" />
      </View>
      <Text style={styles.statCardValue}>{value}</Text>
      <Text style={styles.statCardLabel}>{label}</Text>
    </View>
  );

  // Fix: Create styles after darkMode state is known
  const styles = StyleSheet.create({
    safeArea: {
      flex: 1,
      backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 20,
      paddingVertical: 16,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 8,
    },
    backButton: {
      padding: 8,
    },
    headerTitleContainer: {
      flex: 1,
      marginHorizontal: 16,
    },
    headerTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: '#ffffff',
      textShadowColor: 'rgba(0, 0, 0, 0.3)',
      textShadowOffset: { width: 1, height: 1 },
      textShadowRadius: 3,
    },
    headerSubtitle: {
      fontSize: 12,
      color: 'rgba(255, 255, 255, 0.9)',
      marginTop: 4,
    },
    favoriteButton: {
      padding: 8,
    },
    container: {
      flex: 1,
    },
    content: {
      flex: 1,
    },
    rankingContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 20,
      paddingVertical: 16,
      backgroundColor: darkMode ? '#2d3748' : '#ffffff',
      borderBottomWidth: 1,
      borderBottomColor: darkMode ? '#4a5568' : '#e5e7eb',
    },
    rankingBadge: {
      width: 60,
      height: 60,
      borderRadius: 30,
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 16,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.2,
      shadowRadius: 4,
      elevation: 4,
    },
    rankingNumber: {
      fontSize: 24,
      fontWeight: 'bold',
      color: '#ffffff',
    },
    rankingLabel: {
      fontSize: 10,
      color: '#ffffff',
      marginTop: 2,
    },
    rankingInfo: {
      flex: 1,
    },
    rankingText: {
      fontSize: 16,
      fontWeight: '600',
      color: darkMode ? '#f7fafc' : '#1f2937',
      marginBottom: 4,
    },
    worldRanking: {
      fontSize: 14,
      color: darkMode ? '#a0aec0' : '#6b7280',
    },
    quickStatsContainer: {
      flexDirection: 'row',
      backgroundColor: darkMode ? '#2d3748' : '#ffffff',
      paddingVertical: 16,
      marginBottom: 1,
    },
    statItem: {
      flex: 1,
      alignItems: 'center',
    },
    statDivider: {
      width: 1,
      backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    },
    statValue: {
      fontSize: 16,
      fontWeight: 'bold',
      color: darkMode ? '#f7fafc' : '#1f2937',
      marginTop: 4,
    },
    statLabel: {
      fontSize: 12,
      color: darkMode ? '#a0aec0' : '#6b7280',
      marginTop: 2,
    },
    actionButtonsContainer: {
      flexDirection: 'row',
      paddingHorizontal: 20,
      paddingVertical: 16,
      backgroundColor: darkMode ? '#2d3748' : '#ffffff',
      borderBottomWidth: 1,
      borderBottomColor: darkMode ? '#4a5568' : '#e5e7eb',
    },
    actionButton: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 12,
      borderRadius: 8,
      marginHorizontal: 4,
    },
    websiteButton: {
      backgroundColor: '#3b82f6',
    },
    applyButton: {
      backgroundColor: '#10b981',
    },
    shareButton: {
      backgroundColor: '#8b5cf6',
    },
    actionButtonText: {
      color: '#ffffff',
      fontSize: 14,
      fontWeight: '600',
      marginLeft: 8,
    },
    section: {
      backgroundColor: darkMode ? '#2d3748' : '#ffffff',
      marginBottom: 8,
      paddingHorizontal: 20,
      paddingVertical: 16,
    },
    sectionHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    sectionTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      color: darkMode ? '#f7fafc' : '#1f2937',
      marginBottom: 12,
    },
    descriptionText: {
      fontSize: 14,
      lineHeight: 22,
      color: darkMode ? '#cbd5e0' : '#4b5563',
    },
    specializationsContainer: {
      marginTop: 8,
    },
    specializationItem: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: darkMode ? '#4a5568' : '#f3f4f6',
    },
    specializationText: {
      fontSize: 14,
      color: darkMode ? '#cbd5e0' : '#4b5563',
      marginLeft: 12,
      flex: 1,
    },
    detailsContainer: {
      marginTop: 8,
    },
    detailRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: darkMode ? '#4a5568' : '#f3f4f6',
    },
    detailIconContainer: {
      width: 32,
      height: 32,
      borderRadius: 16,
      backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 12,
    },
    detailContent: {
      flex: 1,
    },
    detailLabel: {
      fontSize: 12,
      color: darkMode ? '#a0aec0' : '#6b7280',
      marginBottom: 2,
    },
    detailValue: {
      fontSize: 14,
      fontWeight: '600',
      color: darkMode ? '#f7fafc' : '#1f2937',
    },
    contactButton: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: '#667eea',
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderRadius: 8,
      justifyContent: 'center',
      marginTop: 16,
    },
    contactButtonText: {
      color: '#ffffff',
      fontSize: 14,
      fontWeight: '600',
      marginLeft: 8,
    },
    statsGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginTop: 8,
      marginHorizontal: -4,
    },
    statCard: {
      width: '33.33%',
      padding: 8,
      alignItems: 'center',
    },
    statIconContainer: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: 8,
    },
    statCardValue: {
      fontSize: 14,
      fontWeight: 'bold',
      color: darkMode ? '#f7fafc' : '#1f2937',
      marginBottom: 2,
    },
    statCardLabel: {
      fontSize: 10,
      color: darkMode ? '#a0aec0' : '#6b7280',
      textAlign: 'center',
    },
    alumniContainer: {
      marginTop: 8,
    },
    alumnusItem: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: darkMode ? '#4a5568' : '#f3f4f6',
    },
    alumnusText: {
      fontSize: 14,
      color: darkMode ? '#cbd5e0' : '#4b5563',
      marginLeft: 12,
      flex: 1,
    },
    mapContainer: {
      height: 200,
      borderRadius: 12,
      overflow: 'hidden',
      marginBottom: 8,
    },
    map: {
      flex: 1,
    },
    mapMarker: {
      alignItems: 'center',
    },
    markerGradient: {
      width: 40,
      height: 40,
      borderRadius: 20,
      justifyContent: 'center',
      alignItems: 'center',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.3,
      shadowRadius: 4,
      elevation: 4,
    },
    directionsButton: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: '#667eea',
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderRadius: 8,
      justifyContent: 'center',
      marginTop: 8,
    },
    directionsButtonText: {
      color: '#ffffff',
      fontSize: 14,
      fontWeight: '600',
      marginLeft: 8,
    },
    tagsContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginTop: 8,
      marginHorizontal: -4,
    },
    tag: {
      backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 16,
      margin: 4,
      flexDirection: 'row',
      alignItems: 'center',
    },
    russellTag: {
      backgroundColor: '#002147',
    },
    scholarshipTag: {
      backgroundColor: '#059669',
    },
    tagText: {
      fontSize: 12,
      color: darkMode ? '#cbd5e0' : '#4b5563',
    },
    russellTagText: {
      color: '#ffffff',
      marginLeft: 4,
    },
    scholarshipTagText: {
      color: '#ffffff',
      marginLeft: 4,
    },
  });

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor={university.color} />
      
      {renderHeader()}
      
      <ScrollView 
        style={styles.container}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.content}>
          {renderRankingBadge()}
          {renderQuickStats()}
          {renderActionButtons()}
          {renderDescription()}
          {renderSpecializations()}
          {renderAdmissionDetails()}
          {renderStudentLife()}
          {renderNotableAlumni()}
          {renderUniversityMap()}
          {renderTags()}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

export default UniversityDetails;